// Angular
import { Router } from '@angular/router';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

// RXJS & NGRX
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { take, map, catchError, finalize } from 'rxjs/operators';

import * as fromRoot from '../../../reducers';
import * as olci from '../../../actions/olci';
import { constants } from '../../../constants';
import * as tracking from '../../../actions/tracking';
import { LoaderService } from '../../loading/loader.service';
import { GuestService } from '../../core/services/guest.service';
import { BookingService } from '../../core/services/booking.service';
import { GatingServices } from '../../core/services/gating.services';
import { MicroServicesService } from '../../core/services/micro-services.service';

@Component({
    selector: 'app-exit-confirmation',
    templateUrl: './modal-exit-confirmation.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ModalExitConfirmationComponent implements OnChanges, OnDestroy {
  @Input() public layoutState: any;
  @Input() public bundle: any;
  public exitConfirmationModal: boolean;

  public constructor(
    private readonly store: Store<fromRoot.AppState>,
    private readonly gatingServices: GatingServices,
    private readonly bookingService: BookingService,
    private readonly loader: LoaderService,
    private microServices: MicroServicesService,
    private readonly guestService: GuestService,
    private readonly router: Router
  ) {
    this.exitConfirmationModal = false;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['layoutState']) {
      const layoutState = changes['layoutState'].currentValue;

      if (layoutState && layoutState.openedModalName === constants.MODAL.EXIT) {
        if (this.router.url !== constants.ROUTES.CONFIRMATION_PAGE) {
          this.exitConfirmationModal = true;
          this.store.dispatch(new tracking.TrackPageInteractionEventAction('modal: exit confirmation: leaving check-in?'));
          const inert = document.createAttribute('inert');
          document.getElementById('main')?.setAttributeNode(inert);
        } else {
          this.loader.showLoader();
          this.endSession();
        }
      } else if (layoutState && layoutState.openedModalName === 'close') {
        this.exitConfirmationModal = false;
        document.getElementById('main')?.removeAttribute('inert');
      }
    }
  }

  public ngOnDestroy(): void {
    document.getElementById('main')?.removeAttribute('inert');
    setTimeout(() => {
      document.getElementById('my-account-button')?.focus();
    }, 100);
  }

  private endSession(): void {
    const locationHref = this.layoutState.exitUrl ? this.layoutState.exitUrl : this.gatingServices.getGAAccountURL();
    this.endSessionOnMidlewareAndMicroservice()
      .pipe(take(1))
      .subscribe((ok) => {
        // Should be on marked order, otherwise some screens will fail to exit.
        // 1. Close guest accounts session if needed
        if (this.layoutState.logoutFromGA) {
          this.bookingService.guestAccountsClearCookies();

          // Give browser a time to clear cookies
          setTimeout(() => {
            // Special case: used only for "sign out" option
            this.bookingService?.guestAccountsLogout()?.pipe(
              finalize(()=>{
                 // 2. Redirect on client
                 (<any>window).location.href = this.layoutState.exitUrl;
                 // 3. Clear the store
                 this.store.dispatch(new olci.ClearStateOnlyAction());
              })
            ).subscribe();
          }, 500);
        } else {
          // Standard case: used for all except "sign out" option
          // 1. Redirect on client
          (<any>window).location.href = locationHref;

          // 2. Empty the store
          this.store.dispatch(new olci.ClearStateOnlyAction());
        }
      });
  }

  private sessionEndMicroservice(): Observable<any> {
    const selectedGuests = this.guestService.getAllGuestsBookingCombination(
      this.guestService.selectedForCheckin
    );
    const payload = {
      checkinToken: this.bookingService.checkinToken,
      guests: selectedGuests,
    };
    return this.microServices.guestCheckinEnd(payload).pipe(
      map((resp) => {
        return resp;
      }),
      catchError((error) => {
        return of(error);
      })
    );
  }
  
  private endSessionOnMidlewareAndMicroservice(): Observable<any> {
    return this.sessionEndMicroservice()
      .pipe(
        map((resp) => resp),
        catchError((error) => of(error))
      );
  }

  public yesContinue(): void {
    this.store.dispatch(
      new tracking.TrackPageCustomInteractionEventAction({
        event: 'leaveCheckin',
        interaction: 'modal: exit confirmation: yes',
      })
    );
    this.loader.showLoader();
    window.localStorage.removeItem(
      constants.LOCAL_STORAGE_KEYS.MOBILE_AD_MODAL
    );
    this.endSession();
  }

  public closeModal(): void {
    this.exitConfirmationModal = false;
    setTimeout(() => {
      this.store.dispatch(new olci.CloseModalAction());
    }, 0);
    this.store.dispatch(
      new tracking.TrackPageCustomInteractionEventAction({
        event: 'stayCheckin',
        interaction: 'modal: exit confirmation: no',
      })
    );
  }
}
